import type { AgentAttachment } from '@commenda/types';
import { pushFileToS3Url } from '@commenda/utils/files/uploadFile';
import type { UIMessage } from 'ai';
import {
  createAgentConversation,
  deleteAgentConversation,
  hydrateEnvelopeMessages,
  listAgentConversations,
  readAgentConversation,
  renameAgentConversation,
  setAgentConversationVisibility,
  stopAgentConversation,
} from './agentConversationsApi';
import {
  agentAttachmentDownloadUrl,
  createAgentAttachmentUpload,
  fetchAgentReferencePreview,
  resolveAgentAttachmentViewUrl,
} from './agentPlatformApi';
import {
  COMMENDA_AI_ATTACHMENT_MIME_TYPE_BY_EXTENSION,
  COMMENDA_CHAT_PROFILE_KEY,
  WORKFLOW_BUILDER_PROFILE_KEY,
} from './constants';
import type {
  AgentConversationReadOptions,
  AgentConversationScope,
  AgentConversationSummary,
  CommendaAiConversationVisibility,
  CommendaAiMutationResult,
  CommendaAiReferencePreview,
  CommendaAiUploadFile,
} from './types';
import { CommendaAiReferenceType } from './types';

export type CommendaAiWorkspaceId = number | null;

/** Firm scope: a conversation with no company, spanning an affiliate member's clients. Sent as
 * `scope=firm` so the backend can tell it from "no filter" (which a null company means today). */
export type CommendaAiScope = AgentConversationScope;

type CommendaAiConversationsKey = readonly [
  'commenda-ai/conversations',
  CommendaAiWorkspaceId,
  CommendaAiScope?,
];

/** The scope is only part of the key when set, so every existing two-element consumer keeps the
 * same cache entry and a firm list never shares one with a client list. */
export const commendaAiConversationsKey = (
  companyId: CommendaAiWorkspaceId,
  isWorkspaceResolved: boolean,
  scope?: CommendaAiScope
): CommendaAiConversationsKey | null => {
  if (!isWorkspaceResolved) return null;
  return scope
    ? ['commenda-ai/conversations', companyId, scope]
    : ['commenda-ai/conversations', companyId];
};

export const commendaAiFeedbackKey = (
  conversationId: string,
  companyId: CommendaAiWorkspaceId,
  isWorkspaceResolved: boolean
) =>
  isWorkspaceResolved
    ? (['commenda-ai/feedback', conversationId, companyId] as const)
    : null;

/** Read side effects the backend records: which run of a scheduled agent was opened, and from
 * where. Only an email link knows either, so an ordinary open sends neither. */
export type CommendaAiRunContext = AgentConversationReadOptions;

export const commendaAiAttachmentDownloadUrl = agentAttachmentDownloadUrl;

export const commendaAiDocumentProcessingKey = (conversationId: string) =>
  ['agent/document-processing', conversationId] as const;

/** sessionStorage key holding the first message typed on the new-chat screen. It's flushed (sent)
 * once the freshly-created conversation view mounts — this is how "create on send" avoids ever
 * spawning an empty conversation. */
export const commendaAiPendingKey = (conversationId: string) =>
  `commenda-ai:pending:${conversationId}`;

export interface CommendaAiConversation {
  id: string;
  /** The owner: the user who started it. */
  userId: string;
  createdByName?: string | null;
  title: string | null;
  status: string;
  visibility: CommendaAiConversationVisibility;
  updatedAt: string;
  agentType?: string;
  lastActivityAt?: string | null;
  lastReadAt?: string | null;
  scheduledAgentId?: string | null;
  /** The company it belongs to. Null for a builder, research or staff conversation, which the backend
   * never lets anyone share, so the sharing control is hidden for it. */
  companyId?: number | null;
  /** Per-conversation facts the backend stores. Carries `profile` when the conversation was opened
   * against a chosen agent; the agent is fixed at creation, so this is what it runs as. */
  metadata?: Record<string, unknown> | null;
}

/** The agent a conversation runs as, or null when it runs the default chat profile. */
export function getBoundProfileKey(
  conversation: Pick<CommendaAiConversation, 'metadata'> | null
): string | null {
  const stored = conversation?.metadata?.profile;
  return typeof stored === 'string' && stored.length > 0 ? stored : null;
}

const ATTACHMENT_PROFILE_KEYS: ReadonlySet<string> = new Set([
  COMMENDA_CHAT_PROFILE_KEY,
  WORKFLOW_BUILDER_PROFILE_KEY,
]);

/** Whether a conversation running as `profileKey` (null is the default chat) takes files; the send
 * refuses attachments for every other agent. */
export function profileTakesAttachments(profileKey: string | null): boolean {
  return profileKey === null || ATTACHMENT_PROFILE_KEYS.has(profileKey);
}

export function getBrowserTimeZone(): string | undefined {
  return Intl.DateTimeFormat().resolvedOptions().timeZone || undefined;
}

export const SCHEDULED_AGENT_TYPE = 'SCHEDULED_AGENT';

export function isConversationUnread(
  conversation: CommendaAiConversation
): boolean {
  if (!conversation.lastActivityAt) return false;
  if (!conversation.lastReadAt) return true;
  return (
    new Date(conversation.lastActivityAt) > new Date(conversation.lastReadAt)
  );
}

/**
 * A unified conversation row → the record every chat surface renders.
 *
 * The unified summary is a strict key allowlist, so the access and read-state fields are optional
 * on it. A row that omits one reads as the safe default: nobody owns it, so rename, delete and the
 * visibility control stay hidden rather than being offered to a reader who cannot use them.
 */
function toCommendaAiConversation(
  summary: Readonly<AgentConversationSummary>
): CommendaAiConversation {
  // The summary resolves the binding into `profile`, where the default chat profile is a value
  // rather than an absence. `metadata.profile` means "a chosen agent", so the default maps back to
  // no choice and a surface showing the bound agent keeps its own default label. Derived rather
  // than passed through, so the label cannot change if the read starts returning the raw column.
  const chosenProfile =
    summary.profile && summary.profile !== COMMENDA_CHAT_PROFILE_KEY
      ? summary.profile
      : null;

  return {
    id: summary.conversationId,
    userId: summary.userId ?? '',
    createdByName: summary.createdByName ?? null,
    title: summary.title,
    status: summary.status,
    visibility: summary.visibility ?? 'PRIVATE',
    updatedAt: summary.updatedAt,
    agentType: summary.agentType ?? summary.profile ?? undefined,
    lastActivityAt: summary.lastActivityAt,
    lastReadAt: summary.lastReadAt ?? null,
    scheduledAgentId: summary.scheduledAgentId ?? null,
    companyId: summary.companyId ?? null,
    metadata: chosenProfile ? { profile: chosenProfile } : null,
  };
}

/** Resolve a fresh inline (browser-viewable) URL for an attachment — used by the preview panel and
 * image thumbnails. Works for just-uploaded files (whose stored URL isn't signed yet) and re-signs
 * old chats so previews never expire in place. */
export async function resolveCommendaAiAttachmentViewUrl(
  fileUrl: string
): Promise<string> {
  return resolveAgentAttachmentViewUrl(fileUrl);
}

/** A chat conversation is bound to the chat profile at creation, or to the agent the surface let
 * the reader choose. The binding is fixed from here on: the send re-checks it and never rewrites it. */
export async function createCommendaAiConversation(
  companyId: CommendaAiWorkspaceId,
  options?: { scope?: CommendaAiScope; profile?: string }
): Promise<CommendaAiMutationResult<CommendaAiConversation>> {
  const created = await createAgentConversation({
    profile: [options?.profile ?? COMMENDA_CHAT_PROFILE_KEY],
    ...(companyId === null ? {} : { companyId }),
    ...(options?.scope ? { scope: options.scope } : {}),
  });

  if (!created.success || !created.data) return { ...created, data: undefined };

  return {
    success: true,
    data: toCommendaAiConversation(created.data.conversation),
  };
}

export async function listCommendaAiConversations(
  companyId: CommendaAiWorkspaceId,
  scope?: CommendaAiScope
): Promise<CommendaAiConversation[]> {
  // Filtered by profile because the unified list spans every profile the caller can reach, and by
  // run sessions because a scheduled agent's runs are that agent's history rather than threads here.
  const { conversations } = await listAgentConversations({
    profile: COMMENDA_CHAT_PROFILE_KEY,
    companyId,
    excludeRunSessions: true,
    ...(scope ? { scope } : {}),
  });

  return conversations.map(toCommendaAiConversation);
}

/**
 * The conversation plus its history, already resolved into AI SDK messages: the unified read
 * returns `parts` for every stored dialect, so there is no per-surface hydration left to do.
 *
 * No workspace or scope argument, unlike the list: the read authorizes the conversation against
 * the caller directly, including the firm-membership check a firm-scope thread needs.
 */
export async function getCommendaAiConversation(
  id: string,
  runContext?: CommendaAiRunContext
): Promise<{ conversation: CommendaAiConversation; messages: UIMessage[] }> {
  const envelope = await readAgentConversation(id, undefined, runContext);

  return {
    conversation: toCommendaAiConversation(envelope.conversation),
    messages: hydrateEnvelopeMessages(envelope),
  };
}

/** The workspace and scope arguments are inert on the unified routes, which resolve the
 * conversation from its id and authorize the caller against it. Kept on the signature so every
 * caller of the old workspace-scoped write keeps compiling; underscored so they read as unused. */
export async function deleteCommendaAiConversation(
  id: string,
  _companyId: CommendaAiWorkspaceId,
  _scope?: CommendaAiScope
): Promise<CommendaAiMutationResult> {
  return deleteAgentConversation(id);
}

export async function renameCommendaAiConversation(
  id: string,
  title: string,
  _companyId: CommendaAiWorkspaceId,
  _scope?: CommendaAiScope
): Promise<CommendaAiMutationResult> {
  const renamed = await renameAgentConversation(id, title);
  return { ...renamed, data: undefined };
}

export async function updateCommendaAiConversationVisibility(
  id: string,
  visibility: CommendaAiConversationVisibility
): Promise<CommendaAiMutationResult> {
  return setAgentConversationVisibility(id, visibility);
}

export async function stopCommendaAiConversation(
  id: string
): Promise<CommendaAiMutationResult> {
  return stopAgentConversation(id);
}

/** Upload a chat attachment to S3 via the shared agent presigned endpoint. The key is derived from
 * the caller's own id, so the upload can only land in their namespace. A file from an uploaded folder is
 * named by its path, which is how the agent learns the folder structure. */
export async function uploadCommendaAiAttachment({
  file,
  path,
}: CommendaAiUploadFile): Promise<CommendaAiMutationResult<AgentAttachment>> {
  const uploadError = `Could not upload ${path}.`;

  try {
    const sanitizedName = file.name.replaceAll(/[^a-zA-Z0-9!\-_.*'()]/g, '_');
    const mimeType =
      file.type ||
      COMMENDA_AI_ATTACHMENT_MIME_TYPE_BY_EXTENSION[
        file.name.split('.').pop()?.toLowerCase() ?? ''
      ] ||
      '';
    const presigned = await createAgentAttachmentUpload(
      sanitizedName,
      mimeType
    );
    if (!presigned.success || !presigned.data)
      return {
        success: false,
        error: presigned.error ?? uploadError,
        ...(presigned.status ? { status: presigned.status } : {}),
      };

    const putResult = await pushFileToS3Url(
      presigned.data.signedUrl,
      new File([file], file.name, { type: mimeType })
    );
    if (!putResult.ok) return { success: false, error: uploadError };

    return {
      success: true,
      data: {
        s3Key: presigned.data.s3Key,
        url: presigned.data.hostedUrl,
        fileName: path,
        mimeType,
        sizeBytes: file.size,
      },
    };
  } catch (error: unknown) {
    return {
      success: false,
      error: error instanceof Error ? error.message : uploadError,
    };
  }
}

export async function fetchCommendaAiReferencePreview(
  type: CommendaAiReferenceType,
  referenceId: string
): Promise<CommendaAiReferencePreview> {
  return fetchAgentReferencePreview(type, referenceId);
}
